import { motion } from "framer-motion";

const packages = [
  {
    id: "essentials",
    name: "Essentials",
    price: "$450",
    duration: "2 hours",
    tag: "Intimate gatherings",
    features: [
      "Mirror Photo Booth",
      "Unlimited digital sharing",
      "Unlimited on-site prints",
      "1 custom template · 2x6 strips",
      "Backdrop of choice · red-carpet prop",
      "Props styled for your event",
      "Unlimited boomerangs & GIFs",
      "DSLR camera · booth attendants · full set-up",
    ],
    img: "https://bc-user-uploads.brandcrowd.com/public/media-Production/4e66984e-ccb1-4743-b5bf-abeb6dbac48e/7b61475c-2826-480e-b984-20372cd7674b_2x",
    span: "md:col-span-4",
    aspect: "aspect-[4/5]",
  },
  {
    id: "professional",
    name: "Professional",
    price: "$650",
    duration: "3 hours",
    tag: "Most booked",
    highlight: true,
    features: [
      "Everything in Essentials",
      "2 custom templates",
      "Choice of 4x6 or 2x6 strip prints",
      "Magazine template keepsake",
      "Extended run-time for bigger crowds",
      "Booth attendants throughout",
    ],
    img: "https://customer-assets-0z36b82j.emergentagent.net/job_memory-booth-8/artifacts/sjnm429j_IMG_4471.jpeg",
    span: "md:col-span-5",
    aspect: "aspect-[4/6]",
    fitContain: true,
  },
  {
    id: "enterprise",
    name: "Enterprise",
    price: "$850",
    duration: "4 hours",
    tag: "Weddings & Quinces",
    features: [
      "Everything in Professional",
      "Audio guest phone book rental",
      "Full digital pictures of the event",
      "USB drive delivered post-event",
      "Priority scheduling",
      "Full 4-hour coverage",
    ],
    img: "https://bc-user-uploads.brandcrowd.com/public/media-Production/4e66984e-ccb1-4743-b5bf-abeb6dbac48e/900df070-157c-48f6-979b-f7d759b43721_2x",
    span: "md:col-span-3",
    aspect: "aspect-[3/4]",
    fitContain: true,
  },
];

const reveal = {
  initial: { y: 40, opacity: 0 },
  whileInView: { y: 0, opacity: 1 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
};

export default function Packages() {
  return (
    <section id="packages" className="relative bg-[#0A0A0A] text-[#FDFBF7] py-24 md:py-40 grain" data-testid="packages-section">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <motion.div {...reveal} className="flex items-end justify-between mb-14 md:mb-20">
          <div>
            <div className="overline text-[#FDFBF7]/60 mb-4">The Packages</div>
            <h2 className="font-serif font-light text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight max-w-3xl">
              Three ways to <em className="text-[#FF5722]">celebrate</em>.
            </h2>
          </div>
          <p className="hidden md:block max-w-xs text-sm text-[#FDFBF7]/70">
            Every package is fully customisable — tell us about your event and we&apos;ll tailor the studio.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
          {packages.map((p, i) => (
            <motion.article
              key={p.id}
              {...reveal}
              transition={{ ...reveal.transition, delay: i * 0.08 }}
              className={`${p.span} group relative border border-white/10 rounded-sm overflow-hidden bg-black/40 flex flex-col`}
              data-testid={`package-card-${p.id}`}
            >
              <div className="relative">
                <div
                  className={`w-full ${p.aspect} ${p.fitContain ? "bg-contain bg-no-repeat" : "bg-cover"} bg-center bg-[#111] transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105`}
                  style={{ backgroundImage: `url(${p.img})` }}
                />
                {!p.fitContain && (
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/10 pointer-events-none" />
                )}
                {p.highlight && (
                  <div className="absolute top-4 right-4 px-3 py-1.5 rounded-full bg-[#FF5722] text-[#FDFBF7] text-[10px] tracking-widest uppercase">
                    Most booked
                  </div>
                )}
                <div className={`absolute bottom-0 left-0 right-0 flex items-end justify-between px-4 pt-8 pb-4 ${p.fitContain ? "bg-gradient-to-t from-black via-black/80 to-transparent" : ""}`}>
                  <div>
                    <div className="overline opacity-80">{p.tag}</div>
                    <div className="font-serif text-3xl md:text-4xl leading-tight">{p.name}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-serif text-3xl md:text-4xl">{p.price}</div>
                    <div className="overline opacity-70">{p.duration}</div>
                  </div>
                </div>
              </div>
              <div className="p-6 md:p-8 flex-1 flex flex-col">
                <ul className="space-y-3 text-sm text-[#FDFBF7]/85">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-3">
                      <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#FF5722]" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href="#booking"
                  onClick={() => {
                    try {
                      window.localStorage.setItem("pb_selected_package", p.id);
                    } catch (_e) {
                      // ignore storage errors
                    }
                  }}
                  className="mt-8 self-start inline-flex items-center gap-3 text-sm tracking-wide border-b border-[#FDFBF7]/60 pb-1 hover:border-[#FF5722] hover:text-[#FF5722] transition-colors"
                  data-testid={`select-package-${p.id}`}
                >
                  Select this package
                  <span>→</span>
                </a>
              </div>
            </motion.article>
          ))}
        </div>

        {/* Add-Ons */}
        <motion.div {...reveal} className="mt-20 md:mt-28 border-t border-white/10 pt-14">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="overline text-[#FDFBF7]/60 mb-3">Add-ons</div>
              <h3 className="font-serif text-4xl md:text-5xl leading-tight">Make it <em className="text-[#FF5722]">yours</em>.</h3>
            </div>
            <p className="hidden md:block max-w-xs text-sm text-[#FDFBF7]/60">
              Layer any of these on top of any package.
            </p>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6" data-testid="addons-list">
            {[
              { l: "Audio guest phone book", p: "+$150" },
              { l: "Full digital pictures of event", p: "+$150" },
              { l: "Memory album (4x6 & 2x6)", p: "+$130" },
              { l: "Connect 4 game", p: "+$100" },
              { l: "5 custom props", p: "+$100" },
              { l: "Additional templates", p: "+$50 each" },
              { l: "Circle keychains · 50 ct", p: "+$250" },
              { l: "Circle keychains · 100 ct", p: "+$450" },
              { l: "Strip keychains · 50 ct", p: "+$250" },
              { l: "Strip keychains · 100 ct", p: "+$450" },
              { l: "Custom sports cards · 50 ct · soft + hard sleeve", p: "+$300" },
              { l: "Custom sports cards · 100 ct · soft + hard sleeve", p: "+$500" },
              { l: "Acrylic magnet card holder · 50 ct", p: "+$200" },
              { l: "Acrylic magnet card holder · 100 ct", p: "+$400" },
              { l: "PSA full slabs · 50 ct", p: "+$300" },
              { l: "PSA full slabs · 100 ct", p: "+$500" },
            ].map((a) => (
              <li
                key={a.l}
                className="flex items-center justify-between border border-white/10 rounded-sm px-5 py-4 bg-black/30 hover:border-[#FF5722] transition-colors"
              >
                <span className="text-sm text-[#FDFBF7]/90">{a.l}</span>
                <span className="font-serif italic text-lg text-[#FF5722]">{a.p}</span>
              </li>
            ))}
          </ul>
          <p className="mt-6 text-xs text-[#FDFBF7]/50">
            Audio voicemails & digital pictures are delivered on a USB drive (provided).
          </p>
        </motion.div>
      </div>
    </section>
  );
}
